import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { trackFormSubmission } from '../../../firebase/config';

const RequestForm = () => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [transportType, setTransportType] = useState('road');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  const vehicleTypes = [
    { value: 'frigo', label: t('form.vehicles.frigo') },
    { value: 'mega', label: t('form.vehicles.mega') },
    { value: 'standard', label: t('form.vehicles.standard') },
    { value: 'jumbo', label: t('form.vehicles.jumbo') },
    { value: 'platforma', label: t('form.vehicles.platforma') },
    { value: 'van12', label: t('form.vehicles.van12') },
    { value: 'vanFrigo', label: t('form.vehicles.vanFrigo') },
    { value: 'vanTailLift', label: t('form.vehicles.vanTailLift') },
    { value: 'middleTruck', label: t('form.vehicles.middleTruck') },
    { value: 'coilmulda', label: t('form.vehicles.coilmulda') },
    { value: 'frigoHooks', label: t('form.vehicles.frigoHooks') }
  ];

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // EmailJS configuration - HARDCODED FOR TESTING
      const SERVICE_ID = 'service_xks7mpl';
      const TEMPLATE_ID = 'template_x3y5ppg';
      const PUBLIC_KEY = 'Db4TVI0KWskd3KaaX';

      // Prepare email data
      const emailData = {
        from_name: data.name,
        from_email: data.email,
        phone: data.phone,
        city_from: data.cityFrom,
        city_to: data.cityTo,
        date_from: data.dateFrom || t('form.status.notSpecified'),
        date_to: data.dateTo || t('form.status.notSpecified'),
        weight: data.weight,
        volume: data.volume,
        transport_type: transportType,
        vehicle_type: transportType === 'road' ? data.vehicleType : 'Sea Freight',
        to_email: 'kbtrans.requests@gmail.com', // Your company email
        message: `New transport request from ${data.name}
        
Transport Type: ${transportType === 'road' ? t('form.transportType.road') : t('form.transportType.sea')}
Route: ${data.cityFrom} → ${data.cityTo}
Cargo: ${data.weight}kg, ${data.volume}m³
${transportType === 'road' ? `Vehicle: ${data.vehicleType}` : 'Service: Sea Freight'}
Dates: ${data.dateFrom || t('form.status.flexible')} - ${data.dateTo || t('form.status.flexible')}

Contact: ${data.email}, ${data.phone}`
      };

      // Send email using EmailJS
      console.log('Sending email with data:', {
        SERVICE_ID,
        TEMPLATE_ID,
        PUBLIC_KEY,
        emailData
      });
      
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, emailData, PUBLIC_KEY);
      
      setSubmitStatus('success');
      trackFormSubmission(`request_form_${transportType}`, true);
      reset();
    } catch (error) {
      console.error('Email sending failed:', error);
      console.error('Error details:', {
        status: error.status,
        text: error.text,
        message: error.message
      });
      
      // More specific error handling
      if (error.status === 400) {
        setSubmitStatus('config_error');
      } else if (error.status === 403) {
        setSubmitStatus('auth_error');
      } else {
        setSubmitStatus('error');
      }
      
      // Track failed form submission
      trackFormSubmission(`request_form_${transportType}`, false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="request-form" className="section-padding bg-gray-50">
      <noscript>
        <div className="container-custom py-6 text-center">
          <p>{t('seo.noScript')}</p>
          <a className="underline mr-4" href="tel:+48577083279">+48 577 083 279</a>
          <a className="underline" href="mailto:info@kbtrans.eu">info@kbtrans.eu</a>
        </div>
      </noscript>
      <div className="container-custom">
        <motion.div
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('form.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('hero.subtitle')}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Transport Type Selection */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  {t('form.transportType.title')} *
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="road"
                      checked={transportType === 'road'}
                      onChange={(e) => setTransportType(e.target.value)}
                      className="w-4 h-4 text-orange-accent border-gray-300 focus:ring-orange-accent"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      🚚 {t('form.transportType.road')}
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="sea"
                      checked={transportType === 'sea'}
                      onChange={(e) => setTransportType(e.target.value)}
                      className="w-4 h-4 text-orange-accent border-gray-300 focus:ring-orange-accent"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      🚢 {t('form.transportType.sea')}
                    </span>
                  </label>
                </div>
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('form.name')} *
                  </label>
                  <input
                    type="text"
                    {...register('name', { required: t('form.validation.nameRequired') })}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-accent focus:border-orange-accent transition-colors ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('form.name')}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('form.email')} *
                  </label>
                  <input
                    type="email"
                    {...register('email', { 
                      required: t('form.validation.emailRequired'),
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t('form.validation.emailInvalid')
                      }
                    })}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-accent focus:border-orange-accent transition-colors ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('form.email')}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.phone')} *
                </label>
                <input
                  type="tel"
                  {...register('phone', { required: t('form.validation.phoneRequired') })}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-accent focus:border-orange-accent transition-colors ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={t('form.phone')}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                )}
              </div>

              {/* Cities From/To */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('form.cityFrom')} *
                  </label>
                  <input
                    type="text"
                    {...register('cityFrom', { required: t('form.validation.cityFromRequired') })}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-accent focus:border-orange-accent transition-colors ${
                      errors.cityFrom ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('form.cityFrom')}
                  />
                  {errors.cityFrom && (
                    <p className="mt-1 text-sm text-red-600">{errors.cityFrom.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('form.cityTo')} *
                  </label>
                  <input
                    type="text"
                    {...register('cityTo', { required: t('form.validation.cityToRequired') })}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-accent focus:border-orange-accent transition-colors ${
                      errors.cityTo ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('form.cityTo')}
                  />
                  {errors.cityTo && (
                    <p className="mt-1 text-sm text-red-600">{errors.cityTo.message}</p>
                  )}
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('form.dateFrom')} ({t('form.optional')})
                  </label>
                  <input
                    type="date"
                    {...register('dateFrom')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-accent focus:border-orange-accent transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('form.dateTo')} ({t('form.optional')})
                  </label>
                  <input
                    type="date"
                    {...register('dateTo')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-accent focus:border-orange-accent transition-colors"
                  />
                </div>
              </div>

              {/* Weight and Volume */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('form.weight')} *
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    {...register('weight', { 
                      required: t('form.validation.weightRequired'),
                      min: { value: 0, message: t('form.validation.weightPositive') }
                    })}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-accent focus:border-orange-accent transition-colors ${
                      errors.weight ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0.0"
                  />
                  {errors.weight && (
                    <p className="mt-1 text-sm text-red-600">{errors.weight.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('form.volume')} *
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    {...register('volume', { 
                      required: t('form.validation.volumeRequired'),
                      min: { value: 0, message: t('form.validation.volumePositive') }
                    })}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-accent focus:border-orange-accent transition-colors ${
                      errors.volume ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0.0"
                  />
                  {errors.volume && (
                    <p className="mt-1 text-sm text-red-600">{errors.volume.message}</p>
                  )}
                </div>
              </div>

              {/* Vehicle Type - Only for Road Transport */}
              {transportType === 'road' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('form.vehicleType')} *
                  </label>
                  <select
                    {...register('vehicleType', { 
                      required: transportType === 'road' ? t('form.validation.vehicleRequired') : false 
                    })}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-accent focus:border-orange-accent transition-colors ${
                      errors.vehicleType ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">{t('form.vehicleType')}</option>
                    {vehicleTypes.map((vehicle) => (
                      <option key={vehicle.value} value={vehicle.value}>
                        {vehicle.label}
                      </option>
                    ))}
                  </select>
                  {errors.vehicleType && (
                    <p className="mt-1 text-sm text-red-600">{errors.vehicleType.message}</p>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full btn-primary text-lg py-4 transition-all duration-300 ${
                    isSubmitting 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:shadow-lg transform hover:-translate-y-1'
                  }`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('form.status.processing')}
                    </div>
                  ) : (
                    t('form.submit')
                  )}
                </button>
              </div>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <motion.div
                  initial={false}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg"
                >
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {t('form.status.success')}
                  </div>
                </motion.div>
              )}

              {submitStatus === 'error' && (
                <motion.div
                  initial={false}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg"
                >
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    {t('form.status.error')}
                  </div>
                </motion.div>
              )}

              {submitStatus === 'config_error' && (
                <motion.div
                  initial={false}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-lg"
                >
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {t('form.status.config_error')}
                  </div>
                </motion.div>
              )}

              {submitStatus === 'auth_error' && (
                <motion.div
                  initial={false}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-orange-100 border border-orange-400 text-orange-700 rounded-lg"
                >
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" clipRule="evenodd" />
                    </svg>
                    {t('form.status.auth_error')}
                  </div>
                </motion.div>
              )}
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default RequestForm; 